'use client'

import { t } from '@lingui/core/macro'
import { Box } from '@mui/material'

import { Consultation } from '@/core/graphql/types'
import { PatientDataReuseConsentDialog, VisioConferenceMeeting } from '@/widgets/visio-conference'
import { useIsFR } from '@/hooks/useIsFR'
import { ChimeProvider } from '@/shared/ui/providers/ChimeProvider'

import { useDataReuseConsent } from '../model/useDataReuseConsent'
import { ProfessionalInfoBanner } from './ProfessionalInfoBanner'

type ConsultationLiveViewProps = {
  consultation: Consultation
  patientId: string
}

export function ConsultationLiveView({ consultation, patientId }: ConsultationLiveViewProps) {
  const isFR = useIsFR()
  const { consentCompleted, handleConsentComplete } = useDataReuseConsent(consultation._id)

  if (isFR && !consentCompleted) {
    return (
      <PatientDataReuseConsentDialog
        consultationId={consultation._id}
        onComplete={handleConsentComplete}
        existingConsent={consultation.patientConsent?.dataReuseConsent ?? null}
      />
    )
  }

  const medecinFullName = [consultation.medecin?.userInfo?.name, consultation.medecin?.userInfo?.surname].filter(Boolean).join(' ').trim() || undefined
  const medecinDisplayName = medecinFullName ? t`Dr. ${medecinFullName}` : undefined

  return (
    <ChimeProvider>
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {consultation.medecin && <ProfessionalInfoBanner medecin={consultation.medecin} />}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <VisioConferenceMeeting
            consultationStatus={consultation.status}
            doctorName={medecinDisplayName}
            doctorRole={consultation.medecin?.speciality ?? undefined}
            patientId={patientId}
            room={consultation._id}
            consultationId={consultation._id}
            autoRecording={consultation.autoRecording ?? false}
            recordingConsentPending={consultation.recordingConsentPending ?? false}
            recordingConsent={consultation.patientConsent?.recordingConsent ?? undefined}
            initialPatientLocation={consultation.patientLocation ?? null}
          />
        </Box>
      </Box>
    </ChimeProvider>
  )
}
